import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import { Hammer, Briefcase } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/api";

const MOROCCO_CENTER = [31.7917, -6.5];

export default function MapPage() {
  const { t, catName, money, cityName, jt } = useI18n();
  const nav = useNavigate();
  const [view, setView] = useState("craftsmen");
  const [craftsmen, setCraftsmen] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [cities, setCities] = useState([]);
  const mapRef = useRef(null);
  const cityLayerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/craftsmen").then((r) => setCraftsmen(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
    api.get("/cities/geo").then((r) => setCities(r.data));
  }, []);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    mapRef.current = L.map(containerRef.current, { scrollWheelZoom: true }).setView(MOROCCO_CENTER, 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: 18,
    }).addTo(mapRef.current);
    cityLayerRef.current = L.layerGroup().addTo(mapRef.current);
  }, []);

  useEffect(() => {
    const layer = cityLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    // Pro Stadt ein Standort; darunter die Anzahl (Handwerker bzw. Aufträge je nach Ansicht).
    const byCity = {};
    (view === "craftsmen" ? craftsmen : jobs).forEach((it) => {
      if (it.city) (byCity[it.city] = byCity[it.city] || []).push(it);
    });
    cities.forEach((c) => {
      const list = byCity[c.name] || [];
      const n = list.length;
      const color = view === "craftsmen" ? "#C65338" : "#5C6646";
      const icon = n > 0
        ? L.divIcon({
            className: "",
            html: `<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font:800 14px Manrope,sans-serif">${n}</span></div>`,
            iconSize: [34, 34],
            iconAnchor: [17, 34],
            popupAnchor: [0, -32],
          })
        : L.divIcon({
            className: "",
            html: `<div style="width:20px;height:20px;border-radius:50% 50% 50% 0;background:#F97316;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,.35)"></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 20],
            popupAnchor: [0, -18],
          });
      const m = L.marker([c.lat, c.lng], { icon })
        .bindTooltip(cityName(c.name), { permanent: true, direction: "top", offset: [0, n > 0 ? -34 : -18], className: "city-label" })
        .addTo(layer);
      if (n > 0) {
        const rows = list.slice(0, 15).map((it) => view === "craftsmen"
          ? `<div style="margin-top:6px"><a href="/craftsmen/${it._id}" style="color:#C65338;font-weight:600">${it.name}</a><br/><span style="color:#8C7A70;font-size:12px">${catName(it.trade)} · ⭐ ${Number(it.rating).toFixed(1)}</span></div>`
          : `<div style="margin-top:6px"><a href="/jobs/${it._id}" style="color:#5C6646;font-weight:600">${jt(it, "title")}</a><br/><span style="color:#8C7A70;font-size:12px">${catName(it.category)}${it.budget != null ? " · " + money(it.budget) : ""}</span></div>`).join("");
        const more = n > 15 ? `<div style="margin-top:8px;font-size:12px;color:#8C7A70">+ ${n - 15}</div>` : "";
        m.bindPopup(`<div style="font-family:Manrope,sans-serif;max-height:220px;overflow:auto"><b>${cityName(c.name)} (${n})</b>${rows}${more}</div>`);
      }
    });
  }, [cities, cityName, view, craftsmen, jobs, catName, money, jt]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-[#2D231F]">{t("map.title")}</h1>
          <p className="text-[#8C7A70] mt-2">{t("map.subtitle")}</p>
        </div>
        <div className="inline-flex rounded-full bg-[#F2E8DF] p-1 self-start">
          <button data-testid="map-view-craftsmen" onClick={() => setView("craftsmen")}
            className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === "craftsmen" ? "bg-[#C65338] text-white" : "text-[#5A4B45]"}`}>
            <Hammer className="w-4 h-4" />{t("map.craftsmen")}
          </button>
          <button data-testid="map-view-jobs" onClick={() => setView("jobs")}
            className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === "jobs" ? "bg-[#5C6646] text-white" : "text-[#5A4B45]"}`}>
            <Briefcase className="w-4 h-4" />{t("map.jobs")}
          </button>
        </div>
      </div>
      <div ref={containerRef} data-testid="morocco-map" className="w-full h-[65vh] rounded-2xl overflow-hidden border border-[#EBDDD3] z-0" />
    </div>
  );
}